import { store } from '../state';
import type { GiphyGif } from '../types';

const grid = document.getElementById('gifs') as HTMLDivElement;

type GridState = 'loading' | 'empty' | 'error';

const MESSAGES: Record<GridState, { icon: string; title: string; body: string }> = {
  loading: { icon: '⏳', title: 'Loading GIFs…', body: 'Fetching the good stuff.' },
  empty: { icon: '🔍', title: 'No GIFs found', body: 'Try a different search term.' },
  error: { icon: '⚠️', title: 'Something went wrong', body: 'Could not reach Giphy. Try again.' },
};

/** Show a full-width status panel (loading / empty / error). */
export function renderStatus(state: GridState, detail?: string): void {
  const m = MESSAGES[state];
  grid.innerHTML = `
    <div class="status status--${state}" role="status">
      <div class="status-icon" aria-hidden="true">${m.icon}</div>
      <h3>${m.title}</h3>
      <p>${detail ?? m.body}</p>
    </div>`;
}

/** Render skeleton placeholders while a fetch is in flight. */
export function renderSkeletons(count = 8): void {
  grid.innerHTML = Array.from({ length: count })
    .map(() => '<div class="gif-card skeleton" aria-hidden="true"></div>')
    .join('');
}

function cardMarkup(gif: GiphyGif): string {
  const img = gif.images.fixed_width;
  const fav = store.isFavorite(gif.id);
  const title = gif.title?.trim() || 'Untitled GIF';
  // Reserve aspect ratio to avoid layout shift as GIFs load.
  const ratio = `${img.width} / ${img.height}`;
  return `
    <figure class="gif-card" data-id="${gif.id}">
      <div class="gif-media" style="aspect-ratio:${ratio}">
        <img
          src="${img.url}"
          alt="${escapeAttr(title)}"
          loading="lazy"
          decoding="async"
        />
        <div class="gif-overlay">
          <span class="rating-badge">${gif.rating ? gif.rating.toUpperCase() : 'NR'}</span>
          <div class="gif-actions">
            <button class="icon-btn fav-btn ${fav ? 'is-active' : ''}"
                    data-action="favorite" type="button"
                    aria-pressed="${fav}" aria-label="Toggle favorite" title="Favorite">
              ${fav ? '★' : '☆'}
            </button>
            <button class="icon-btn" data-action="copy" type="button"
                    aria-label="Copy GIF link" title="Copy link">⧉</button>
            <a class="icon-btn" data-action="open" href="${gif.images.original.url}"
               target="_blank" rel="noopener" aria-label="Open full size" title="Open full size">⤢</a>
          </div>
        </div>
      </div>
      <figcaption class="gif-title">${escapeHtml(title)}</figcaption>
    </figure>`;
}

/** Render a set of GIF cards. `append` keeps existing ones (for "load more"). */
export function renderGifs(gifs: GiphyGif[], append = false): void {
  const html = gifs.map(cardMarkup).join('');
  if (append) grid.insertAdjacentHTML('beforeend', html);
  else grid.innerHTML = html;
}

/** Re-render the favorite state of a single card in place. */
export function refreshFavorite(gif: GiphyGif): void {
  const card = grid.querySelector(`.gif-card[data-id="${gif.id}"] .fav-btn`);
  if (!card) return;
  const fav = store.isFavorite(gif.id);
  card.classList.toggle('is-active', fav);
  card.setAttribute('aria-pressed', String(fav));
  card.textContent = fav ? '★' : '☆';
}

/**
 * Wire delegated click handling for card actions.
 * `lookup` resolves a card id back to its GiphyGif (favorites or current results).
 */
export function bindCardActions(lookup: (id: string) => GiphyGif | undefined): void {
  grid.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;
    const actionEl = target.closest<HTMLElement>('[data-action]');
    if (!actionEl) return;
    const card = actionEl.closest<HTMLElement>('.gif-card');
    const id = card?.dataset.id;
    if (!id) return;
    const gif = lookup(id);
    if (!gif) return;

    const action = actionEl.dataset.action;
    if (action === 'favorite') {
      store.toggleFavorite(gif);
      refreshFavorite(gif);
    } else if (action === 'copy') {
      e.preventDefault();
      await copyLink(gif.images.original.url, actionEl as HTMLButtonElement);
    }
    // "open" is a real <a> — let the browser handle it.
  });
}

async function copyLink(url: string, btn: HTMLButtonElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    const prev = btn.textContent;
    btn.textContent = '✓';
    btn.classList.add('copied');
    setTimeout(() => {
      btn.textContent = prev;
      btn.classList.remove('copied');
    }, 1200);
  } catch {
    /* clipboard blocked — ignore */
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}
